/** biome-ignore-all lint/style/noRestrictedImports: not needed */

import { EMPTY_OBJECT } from '@unifyapps/carbon/consts/empty';
import { sanitizeFileUri } from '@unifyapps/carbon/utils/file/sanitizeFileUri';
import { isFileUri } from '@unifyapps/network/utils/fileUri';
// Deep import, not the `components/Snackbar` barrel: the barrel also exports the Joy
// Snackbar + its provider, which drags @unifyapps/ui's theme tree into every consumer of
// this hook. useSnackbar itself is just a useContext.
import useSnackbar from '@unifyapps/ui/components/Snackbar/useSnackbar';
import { isNative } from '@unifyapps/ui/utils/isNative';
import type { Uppy } from '@uppy/core';
import find from 'lodash/find';
import identity from 'lodash/identity';
import omit from 'lodash/omit';
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useLatest } from 'react-use';
import { getUploadError } from './error';
import getPrivateUrl, { type UploadResponse } from './getPrivateUrl';
import {
  type BlobUploadParams,
  type FilesAction,
  FilesActionType,
  type FilesState,
  UploadError,
  type UploadedFile,
  type UppyFileMeta,
  type UppyFileOptions,
  type UppyManager,
} from './types';
import { useUppyContext } from './UppyContext';

export const sanitizeFileUrl = (fileUrl?: string) => {
  if (!fileUrl) return '';

  const decodedFileUrl = decodeURIComponent(fileUrl);
  const removedDoubleSlashUrl = decodedFileUrl.replace(/(?<temp1>[^:]\/)\/+/g, '$1');
  return removedDoubleSlashUrl;
};

function reducer<T extends UploadedFile>(
  state: FilesState<T>,
  action: FilesAction<T>,
): FilesState<T> {
  switch (action.type) {
    case FilesActionType.RemoveFile: {
      return omit(state, action.payload.value);
    }
    case FilesActionType.RenameFile: {
      const { fileId, uppy, name } = action.payload;
      const currentFile = state[fileId];
      const meta = { ...currentFile.meta, name };
      uppy?.setFileMeta(fileId, meta);

      return { ...state, [fileId]: { ...currentFile, meta } };
    }

    case FilesActionType.ClearFiles: {
      const { referenceId, referenceIdMatcher } = action.payload;
      const matchesRef = (fileRefId: string | undefined) => {
        if (!referenceId || !fileRefId) return false;
        return referenceIdMatcher
          ? referenceIdMatcher(fileRefId, referenceId)
          : fileRefId === referenceId;
      };
      const otherFiles = Object.entries(state).reduce<Record<string, T>>((acc, [fileId, file]) => {
        if (!matchesRef(file.meta.referenceId)) {
          acc[fileId] = file;
        }

        return acc;
      }, {});

      return otherFiles;
    }
    case FilesActionType.AddOrUpdateFile: {
      const { fileId, value } = action.payload;

      return { ...state, [fileId]: value };
    }
    case FilesActionType.UpdateFile: {
      const { fileId, value } = action.payload;
      const currentFile = state[fileId];

      return { ...state, [fileId]: { ...currentFile, ...value } };
    }
    case FilesActionType.UpdateFiles: {
      return action.payload.files;
    }
    default:
      return state;
  }
}

function getPreviewBlobUrl(file?: UploadedFile): string {
  // Native only — the web path below is left exactly as it was. On Android
  // `file.data` is an empty placeholder Blob (see fileUriToBlob.native.ts), so
  // createObjectURL yields a 0-byte preview; use the on-disk path instead.
  // Test the RAW value: sanitizeFileUri prepends the scheme to anything that isn't
  // data:/http(s):/content:, so sanitizing first would let a blob: or relative URL
  // through as `file://blob:…`.
  const rawUri = file?.meta?.uri;
  if (isNative() && isFileUri(rawUri)) return sanitizeFileUri(rawUri);

  try {
    return file?.data ? URL.createObjectURL(file.data) : '';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- ignore error
  } catch (_err) {
    return '';
  }
}

function useUppy<T extends UploadedFile>(
  {
    onFileAdded,
    onUploadSuccess,
    onUploadError,
    onProgress,
    onRestrictedFile,
    adaptUploadFile = identity,
    sourceId = '',
    referenceId,
    shouldHandleFile,
    initialFiles,
    isBuilderAsset,
    accessScope,
    referenceIdMatcher,
  }: UppyManager<T>,
  uppyInstance?: Uppy<UppyFileMeta, Record<string, never>> | null,
) {
  const [files, dispatch] = useReducer<FilesState<T>, [FilesAction<T>]>(
    reducer,
    initialFiles ?? EMPTY_OBJECT,
  );

  const { showSnackbar } = useSnackbar();
  const showErrorSnackbar = useCallback(
    (msg?: string) => {
      showSnackbar({
        color: 'error',
        title: msg ?? 'Something Went Wrong',
      });
    },
    [showSnackbar],
  );
  const uppyContext = useUppyContext();
  const uppy = uppyInstance ?? uppyContext?.uppyInstance?.Uppy;
  const latestOnUploadSuccess = useLatest(onUploadSuccess);
  const latestOnUploadError = useLatest(onUploadError);
  const latestOnProgress = useLatest(onProgress);
  const options = (uppy as unknown as { opts: UppyFileOptions } | undefined)?.opts;

  const { meta } = options ?? {};
  const queryMeta = meta?.queryMeta;

  // Default matcher uses strict equality, custom matcher can use startsWith, regex, etc.
  const matchesReferenceId = useCallback(
    (fileRefId: string | undefined) => {
      if (!referenceId || !fileRefId) return false;
      return referenceIdMatcher
        ? referenceIdMatcher(fileRefId, referenceId)
        : fileRefId === referenceId;
    },
    [referenceId, referenceIdMatcher],
  );

  const isUploading = useMemo(
    () => Boolean(find(files, (file) => !file.url && !file.error)),
    [files],
  );

  const removeFile = useCallback(
    (file: T) => {
      uppy?.removeFile(file.id);
      dispatch({ type: FilesActionType.RemoveFile, payload: { value: file.id } });
    },
    [uppy],
  );

  const removeFileById = useCallback(
    (id: string) => {
      uppy?.removeFile(id);
      dispatch({ type: FilesActionType.RemoveFile, payload: { value: id } });
    },
    [uppy],
  );

  const renameFile = useCallback(
    (fileId: string, name: string) => {
      dispatch({ type: FilesActionType.RenameFile, payload: { uppy, fileId, name } });
    },
    [uppy],
  );

  const clearAll = useCallback(() => {
    dispatch({
      type: FilesActionType.ClearFiles,
      payload: { referenceId, referenceIdMatcher },
    });

    const newFiles = Object.entries(files).reduce<Record<string, T>>((acc, [fileId, file]) => {
      if (matchesReferenceId(file.meta.referenceId)) {
        uppy?.removeFile(fileId);
      } else {
        acc[fileId] = file;
      }

      return acc;
    }, {});

    if (Object.keys(newFiles).length === 0) {
      uppy?.cancelAll();
    }
  }, [files, referenceId, uppy, matchesReferenceId, referenceIdMatcher]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: not needed
  useEffect(() => {
    const shouldProcessEvent = (file: UploadedFile) =>
      (!sourceId || sourceId === file.source) && (!shouldHandleFile || shouldHandleFile(file));
    const _onFileAdded = (file) => {
      // GoogleDrivePicker files may not have referenceId at this stage,
      // so we allow them through and attach it via setFileMeta below

      if (
        referenceId &&
        !matchesReferenceId(file.meta.referenceId) &&
        file.source !== 'GoogleDrivePicker'
      ) {
        return;
      }

      if (shouldProcessEvent(file)) {
        uppy?.setFileMeta(file.id, {
          ...file.meta,
          referenceId: file.meta.referenceId ?? referenceId,
          previewUrl: getPreviewBlobUrl(file),
          isBuilderAsset: isBuilderAsset ?? file.meta.isBuilderAsset,
          accessScope: accessScope ?? file.meta.accessScope,
        });
        if (
          referenceId &&
          !matchesReferenceId(file.meta.referenceId) &&
          file.source !== 'GoogleDrivePicker'
        ) {
          return;
        }
        // setFileMeta replaces the file in uppy's state, so the reference this event
        // carries predates the meta written above — re-read it or previewUrl is lost.
        // The Android S3 path emits upload-progress only once, at the end, so this is
        // the only chance to publish the preview before the upload finishes. Native
        // only: web already self-corrects on its next progress event, and keeping it
        // out of that path leaves web behaviour byte-identical.
        const updatedFile = isNative()
          ? ((uppy?.getFile(file.id) as typeof file | undefined) ?? file)
          : file;
        const addedFile = onFileAdded?.(updatedFile as unknown as T) || updatedFile;
        _onProgress(addedFile, updatedFile.progress);
      }
    };

    const _onProgress = (file, progress) => {
      if (!file) return;
      if (
        referenceId &&
        !matchesReferenceId(file.meta.referenceId) &&
        file.source !== 'GoogleDrivePicker'
      ) {
        return;
      }
      if (shouldProcessEvent(file)) {
        const updatedFile = {
          ...file,
          referenceId,
          fileProgress: Math.round(
            ((progress?.bytesUploaded ?? 0) * 100) / (progress?.bytesTotal ?? 1),
          ),
        } as unknown as T;
        latestOnProgress.current?.(updatedFile);
        dispatch({
          type: FilesActionType.AddOrUpdateFile,
          payload: {
            fileId: file.id,
            value: adaptUploadFile({
              ...updatedFile,
            }),
          },
        });
      }
    };

    const _onRestrictedFile = (file, error) => {
      onRestrictedFile?.(file as unknown as T, error ?? new Error('Restricted file error'));
      if (file && shouldProcessEvent(file) && !file.id) {
        file.id = `${Date.now()}`;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- old code
        file.meta = file.meta || {};
        file.meta.name = file.name;
        file.meta.type = file.type;

        if (error) {
          showErrorSnackbar(getUploadError(error));
        }
      }
    };

    const _uploadCompleted = async (file, resp) => {
      if (!file || !shouldProcessEvent(file)) return;
      if (referenceId && !matchesReferenceId(file.meta.referenceId)) {
        return;
      }

      const body = await getPrivateUrl(file, resp, queryMeta);
      const updatedFile = {
        ...file,
        url: body?.uploadURL,
        uploadResponse: body?.body as never,
      } as unknown as T;
      if (latestOnUploadSuccess.current) {
        latestOnUploadSuccess
          .current(updatedFile, {
            removeFileById,
          })
          .then((uploadedFile) => {
            dispatch({
              type: FilesActionType.AddOrUpdateFile,
              payload: {
                fileId: file.id,
                value: uploadedFile,
              },
            });
          })
          .catch((err: Error) => {
            const error = err.message || UploadError.SaveError;
            dispatch({
              type: FilesActionType.UpdateFile,
              payload: { fileId: file.id, value: { error } },
            });
          });
      } else {
        dispatch({
          type: FilesActionType.AddOrUpdateFile,
          payload: {
            fileId: file.id,
            value: updatedFile,
          },
        });
      }
    };

    const onUploadError = (file, error, response) => {
      const fileWithError = {
        ...file,
        error: getUploadError(error) || UploadError.SaveError,
        retryable: true,
      };
      dispatch({
        type: FilesActionType.AddOrUpdateFile,
        payload: {
          fileId: file.id,
          value: fileWithError,
        },
      });
      latestOnUploadError.current?.(fileWithError, {
        removeFileById,
      });
      console.error('upload-error', { fileWithError, error, response });
    };

    const onError = (error: unknown) => console.error('error', error);

    uppy?.on('upload-progress', _onProgress);
    uppy?.on('file-added', _onFileAdded);
    uppy?.on('restriction-failed', _onRestrictedFile);
    uppy?.on('upload-success', _uploadCompleted);
    uppy?.on('upload-error', onUploadError);
    uppy?.on('error', onError);

    return () => {
      uppy?.off('file-added', _onFileAdded);
      uppy?.off('upload-progress', _onProgress);
      uppy?.off('restriction-failed', _onRestrictedFile);
      uppy?.off('upload-success', _uploadCompleted);
      uppy?.off('upload-error', onUploadError);
      uppy?.off('error', onError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ask @Tapas
  }, [uppy]);

  const blobUpload = useCallback(
    ({ blob, name, type, meta }: BlobUploadParams) => {
      const fileId = uppy?.addFile({
        name,
        type,
        data: blob,
        meta,
      });

      return new Promise<UploadedFile>((resolve, reject) => {
        const cleanup = () => {
          uppy?.off('upload-success', onSuccess);
          uppy?.off('upload-error', onError);
        };

        const onSuccess = async (file: UploadedFile, resp?: UploadResponse) => {
          if (file.id !== fileId) return;
          try {
            const response = await getPrivateUrl(file, resp, queryMeta);
            resolve({ ...file, url: response?.uploadURL, uploadResponse: response?.body });
          } catch (err) {
            reject(err);
          } finally {
            cleanup();
          }
        };

        const onError = (file: UploadedFile, error: Error) => {
          if (file.id !== fileId) return;
          reject(error ?? new Error(`Failed to upload ${file.name}`));
          cleanup();
        };

        uppy?.on('upload-success', onSuccess);
        uppy?.on('upload-error', onError);
      });
    },
    [queryMeta, uppy],
  );

  const filteredFiles = useMemo(
    () =>
      Object.entries(files).reduce<FilesState<T>>((acc, [key, file]) => {
        if (matchesReferenceId(file.meta.referenceId) || file.source === 'GoogleDrivePicker') {
          acc[key] = file;
        }

        return acc;
      }, {}),
    [files, matchesReferenceId],
  );

  const updateFiles = useCallback((updatedFiles: FilesState<T>) => {
    dispatch({ type: FilesActionType.UpdateFiles, payload: { files: updatedFiles } });
  }, []);

  const retryFileById = useCallback(
    (id: string) => {
      uppy?.retryUpload(id);
      dispatch({
        type: FilesActionType.UpdateFile,
        payload: {
          fileId: id,
          value: { error: undefined, retryable: false } as unknown as Partial<T>,
        },
      });
    },
    [uppy],
  );

  const retryAll = useCallback(() => {
    uppy?.retryAll();
    for (const fileId in filteredFiles) {
      if (filteredFiles[fileId].error) {
        dispatch({
          type: FilesActionType.UpdateFile,
          payload: {
            fileId,
            value: { error: undefined, retryable: false } as unknown as Partial<T>,
          },
        });
      }
    }
  }, [uppy, filteredFiles]);

  return {
    uppy,
    files: filteredFiles,
    removeFile,
    removeFileById,
    clearAll,
    renameFile,
    isUploading,
    blobUpload,
    updateFiles,
    retryFileById,
    retryAll,
  };
}

export default useUppy;
